import { useState } from 'react';
import { useCustom } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { ReferralStatusBadge } from '@/components/referrals/ReferralStatusBadge';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { Plus, Eye, Share2 } from 'lucide-react';

export interface ReferralCampaign {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  channel?: string;
  reward_model: 'coupon' | 'cash' | 'none';
  referrer_reward_cents: number;
  reward_trigger: 'install' | 'signup' | 'qualified_action' | 'paid';
  status: 'draft' | 'active' | 'paused' | 'ended';
  environment: 'dev' | 'prod';
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
}

interface OverviewResponse {
  totals: { clicks: number; signups: number; qualified: number; connects: number; revenue_cents: number; coupons_claimed: number };
  campaigns: Array<{ id: string; name: string; slug: string; channel: string; status: string; links: number; clicks: number; signups: number; qualified: number; connects: number; revenue_cents: number; coupons_claimed: number }>;
  top_links: Array<{ id: string; code: string; label: string | null; campaign_name: string | null; channel: string | null; clicks: number; signups: number; qualified: number; connects: number; revenue_cents: number }>;
}

export const CHANNEL_LABELS: Record<string, string> = {
  influencer: 'Influencer',
  affiliate: 'Affiliate',
  bio_link: 'Bio link',
  press: 'Press',
  offline_qr: 'Offline / QR',
  email: 'Email',
  paid_ads: 'Paid ads',
  other: 'Other',
};

interface ListResponse {
  rows: ReferralCampaign[];
}

const REWARD_MODEL_LABELS: Record<ReferralCampaign['reward_model'], string> = {
  coupon: 'Coupon',
  cash: 'Cash',
  none: 'None',
};

export function ReferralsListPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAdminPermissions();
  const [statusFilter, setStatusFilter] = useState<string>('');

  const queryParams: Record<string, string> = { limit: '200' };
  if (statusFilter) queryParams.status = statusFilter;

  const { data, isLoading, isError } = useCustom<ListResponse>({
    url: '/v1/admin/referrals/campaigns',
    method: 'get',
    config: { query: queryParams },
  });

  const { data: overviewData } = useCustom<OverviewResponse>({
    url: '/v1/admin/referrals/overview',
    method: 'get',
  });
  const overview = overviewData?.data;
  const funnelByCampaign = new Map((overview?.campaigns ?? []).map((c) => [c.id, c]));

  const rows = data?.data?.rows ?? [];
  const canWrite = hasPermission('growth.referrals.write');

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Share2 className="w-6 h-6" />
            Referrals
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Influencer &amp; friend referral campaigns. Mint share links, track installs, reward referrers.
          </p>
        </div>
        {canWrite && (
          <button
            onClick={() => navigate('/referrals/new')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition"
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </button>
        )}
      </div>

      {/* Channel overview (Paula's dashboard): totals across every campaign */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Clicks', value: overview.totals.clicks },
            { label: 'Signups', value: overview.totals.signups },
            { label: 'Qualified', value: overview.totals.qualified },
            { label: 'Connects ★', value: overview.totals.connects },
            { label: 'Revenue', value: `$${(overview.totals.revenue_cents / 100).toFixed(2)}` },
            { label: 'Coupons used', value: overview.totals.coupons_claimed },
          ].map((k) => (
            <div key={k.label} className="border rounded-lg p-3 bg-card">
              <div className="text-xs text-muted-foreground">{k.label}</div>
              <div className="text-xl font-semibold">{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Top links across all campaigns — best influencer/asset first */}
      {overview && overview.top_links.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-medium text-muted-foreground mb-2">Top links (all channels)</h2>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr className="text-left">
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="px-4 py-2 font-medium">Campaign</th>
                  <th className="px-4 py-2 font-medium">Channel</th>
                  <th className="px-4 py-2 font-medium text-right">Clicks</th>
                  <th className="px-4 py-2 font-medium text-right">Signups</th>
                  <th className="px-4 py-2 font-medium text-right">Connects ★</th>
                  <th className="px-4 py-2 font-medium text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {overview.top_links.slice(0, 8).map((l) => (
                  <tr key={l.id} className="border-b">
                    <td className="px-4 py-2"><code className="font-medium">{l.code}</code>{l.label && <span className="ml-2 text-xs text-muted-foreground">{l.label}</span>}</td>
                    <td className="px-4 py-2 text-muted-foreground">{l.campaign_name || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{CHANNEL_LABELS[l.channel || 'other'] || l.channel}</td>
                    <td className="px-4 py-2 text-right">{l.clicks}</td>
                    <td className="px-4 py-2 text-right">{l.signups}</td>
                    <td className="px-4 py-2 text-right font-medium">{l.connects}</td>
                    <td className="px-4 py-2 text-right">${(l.revenue_cents / 100).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm bg-background"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="ended">Ended</option>
        </select>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Campaign</th>
              <th className="px-4 py-3 font-medium">Channel</th>
              <th className="px-4 py-3 font-medium">Reward</th>
              <th className="px-4 py-3 font-medium text-right">Signups</th>
              <th className="px-4 py-3 font-medium text-right">Connects ★</th>
              <th className="px-4 py-3 font-medium text-right">Revenue</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium w-12"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-destructive">
                  Failed to load referral campaigns.
                </td>
              </tr>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  No referral campaigns yet.{canWrite ? ' Create one to get started.' : ''}
                </td>
              </tr>
            )}
            {rows.map((c) => (
              <tr
                key={c.id}
                className="border-b hover:bg-muted/30 cursor-pointer transition"
                onClick={() => navigate(`/referrals/${c.id}`)}
              >
                <td className="px-4 py-3">
                  <div className="font-medium">{c.name}</div>
                  <code className="text-xs text-muted-foreground">{c.slug}</code>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{CHANNEL_LABELS[c.channel || 'other'] || c.channel}</td>
                <td className="px-4 py-3 text-muted-foreground">
                  {REWARD_MODEL_LABELS[c.reward_model]}
                  {c.reward_model === 'cash' && c.referrer_reward_cents > 0 && (
                    <span className="ml-1">${(c.referrer_reward_cents / 100).toFixed(2)}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">{funnelByCampaign.get(c.id)?.signups ?? 0}</td>
                <td className="px-4 py-3 text-right font-medium">{funnelByCampaign.get(c.id)?.connects ?? 0}</td>
                <td className="px-4 py-3 text-right">${(((funnelByCampaign.get(c.id)?.revenue_cents ?? 0)) / 100).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <ReferralStatusBadge status={c.status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <Eye className="w-4 h-4 inline text-muted-foreground" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
